import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { patientAPI, doctorAPI, nurseAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Save } from 'lucide-react';
import { PersonalInfoSection } from './register/PersonalInfoSection';
import { EmergencyContactSection } from './register/EmergencyContactSection';
import { MedicalInfoSection } from './register/MedicalInfoSection';
import { AssignmentSection } from './register/AssignmentSection';
import { DocumentsSection } from './register/DocumentsSection';
import { ConsentSection } from './register/ConsentSection';
import { PrivacyPolicyModal } from './register/PrivacyPolicyModal';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const PatientRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [referees, setReferees] = useState([]);
  const isSubmittingRef = useRef(false); // Ref to prevent double submission
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    occupation: '',
    emergency_contact: '',
    emergency_phone: '',
    blood_group: '',
    allergies: '',
    medical_history: '',
    patient_type: 'outdoor',
    specialty: '',
    assigned_doctor_id: '',
    assigned_nurse_id: '',
    referred_by_code: '',
    payment_mode: 'cash',
    consent_given: false,  // DPDPA compliance
    is_medico_legal_case: false,  // Medical regulations - 10 year retention
    consultation_waived: false,  // Waive consultation charges
    waive_reason: '',  // Reason for waiving charges
  });

  useEffect(() => {
    fetchStaff();
    fetchReferees();
    fetchPrivacyPolicy();
  }, []);
  
  const fetchPrivacyPolicy = async () => {
    try {
      const response = await api.get('/privacy-policy');
      setPrivacyPolicy(response.data);
    } catch (error) {
      logger.error('Failed to load privacy policy');
    }
  };

  useEffect(() => {
    // Filter doctors by selected specialty
    if (formData.specialty) {
      const filtered = doctors.filter(d => d.department === formData.specialty);
      setFilteredDoctors(filtered);
    } else {
      setFilteredDoctors(doctors);
    }
  }, [formData.specialty, doctors]);

  const fetchStaff = async () => {
    try {
      const [docRes, nurseRes] = await Promise.all([
        doctorAPI.getAll(),
        nurseAPI.getAll(),
      ]);
      setDoctors(docRes.data);
      setNurses(nurseRes.data);
    } catch (error) {
      logger.error('Failed to load staff');
    }
  };

  const fetchReferees = async () => {
    try {
      const response = await api.get('/referees');
      setReferees(response.data);
    } catch (error) {
      logger.error('Failed to load referees');
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Double-check with ref to absolutely prevent duplicate submissions
    if (isSubmittingRef.current || loading) {
      logger.debug('Preventing duplicate submission');
      return;
    }
    
    // Trim and validate full_name
    const trimmedName = (formData.full_name || '').trim();
    if (!trimmedName) {
      toast.error('Patient name is required');
      return;
    }
    
    // Set both ref and state to prevent double submission
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // Build clean data object - only include non-empty values
      const cleanedData = {
        full_name: trimmedName
      };
      
      // Add other fields only if they have actual values (not empty strings)
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'full_name') return;
        if (value && value !== '' && value !== null && value !== undefined) {
          cleanedData[key] = value;
        }
      });
      
      logger.debug('=== PATIENT REGISTRATION ===');
      logger.debug('Submitting:', JSON.stringify(cleanedData));
      
      const response = await patientAPI.create(cleanedData);
      
      logger.debug('Success:', response.data);
      
      // Upload documents if any
      if (uploadedDocs.length > 0) {
        const patientId = response.data.id;
        for (const doc of uploadedDocs) {
          await patientAPI.uploadDocument(patientId, {
            document_type: doc.type,
            file_data: doc.data,
            filename: doc.filename
          });
        }
      }
      
      // Show success message
      toast.success(`Patient registered successfully! ID: ${response.data.patient_id}`);
      
      // Navigate to home/dashboard page
      navigate('/dashboard');
      
    } catch (error) {
      logger.error('Registration error:', error?.response?.data || error);
      
      let errorMsg = 'Failed to register patient';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          const messages = detail.map(e => {
            const msg = e.msg || String(e);
            return msg
              .replace(/^Value error,\s*/i, '')
              .replace(/^Field required$/i, 'A required field is missing');
          });
          errorMsg = [...new Set(messages)].join('. ');
        } else {
          errorMsg = String(detail);
        }
      }
      
      toast.error(errorMsg);
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const handleDocumentUpload = (e, docType) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedDocs([...uploadedDocs, {
          type: docType,
          data: reader.result,
          filename: file.name
        }]);
        toast.success(`${file.name} added`);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDocument = (index) => {
    setUploadedDocs(uploadedDocs.filter((_, i) => i !== index));
  };

  return (
    <div data-testid="patient-register-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/patients')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Register Patient</h1>
            <p className="text-slate-500">Create a new patient record</p>
          </div>
        </div>
      </header>

      <div className="page-content max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <PersonalInfoSection formData={formData} handleChange={handleChange} />
          <EmergencyContactSection formData={formData} handleChange={handleChange} />
          <MedicalInfoSection formData={formData} handleChange={handleChange} />
          <AssignmentSection formData={formData} handleChange={handleChange} filteredDoctors={filteredDoctors} nurses={nurses} referees={referees} />
          <DocumentsSection uploadedDocs={uploadedDocs} handleDocumentUpload={handleDocumentUpload} removeDocument={removeDocument} />
          <ConsentSection formData={formData} handleChange={handleChange} setShowPrivacyModal={setShowPrivacyModal} />

          {/* Submit */}
          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/patients')}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-red-500 hover:bg-red-600 rounded-full"
              disabled={loading || !formData.consent_given}
              data-testid="submit-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Registering...' : 'Register Patient'}
            </Button>
          </div>
        </form>
      </div>
      
      <PrivacyPolicyModal showPrivacyModal={showPrivacyModal} setShowPrivacyModal={setShowPrivacyModal} privacyPolicy={privacyPolicy} handleChange={handleChange} />
    </div>
  );
};

export default PatientRegister;
